/**
 * Invite & Earn (referrals) DTOs.
 *
 * Mirrors the web `GET /api/referrals` payload one-for-one — the mobile
 * route (`GET /api/mobile/v1/referrals`) and the web route both serialise
 * from the SAME `lib/referrals.ts` chokepoint, so this schema is the wire
 * contract for both.
 *
 * `referralCode` is lazily minted on first read (see `ensureReferralCode`),
 * so it is always a non-empty string by the time a caller sees it.
 *
 * All-plans: the referral programme is not entitlement-gated (web parity —
 * the web page carries no plan gate). It IS internal-account only: a
 * contractor-only (portal) login has no subscription to credit, so the
 * mobile route answers 403 rather than minting a code that can never pay out.
 *
 * The referral CLAIM (`POST /api/referrals/claim`, web-only) is a separate
 * surface with its own eligibility rules and refusal codes — see
 * `lib/referrals-response.ts`. It has no mobile client.
 */
import { z } from "zod";

export const ReferralsResponseSchema = z
  .object({
    /** The caller's own referral code — the `?ref=` value. */
    referralCode: z.string().min(1),
    /** Full share URL — `${BASE}/signup?ref=${referralCode}`. */
    referralLink: z.string().url(),
    /** How many users signed up carrying this code. */
    signups: z.number().int().nonnegative(),
    /**
     * Recorded conversions for this code — referred users whose subscription
     * was confirmed paid at least once.
     *
     * #2311: this used to be "how many of those signups are on a paid tier
     * TODAY", inferred live from the referred users' current plan. That was
     * inflated by already-paying visitors who merely claimed the code and
     * deflated the instant one cancelled, so it is now a count of durable
     * `ReferralConversion` events. The WIRE SHAPE is unchanged — a
     * non-negative integer — so no consumer needs updating.
     */
    upgrades: z.number().int().nonnegative(),
  });
export type ReferralsResponse = z.infer<typeof ReferralsResponseSchema>;

// #2411 server-boundary variant: the mobile app parses the permissive base;
// the route serialises through the Strict form.
export const ReferralsResponseStrict = ReferralsResponseSchema.strict();
